// rendermakrdown.js
import { marked } from "marked";
import DOMPurify from "dompurify";
import hljs from "highlight.js";
import "highlight.js/styles/github-dark.css";

/* ------------------ Small helpers ------------------ */
function escapeHtml(text = "") {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/* Repair common streaming/tokenization artifacts:
   - fix spaced protocols like "https ://" -> "https://"
   - collapse spaces around dots in domains "launch crew .paramgroups .com" -> "launchcrew.paramgroups.com"
   - re-join broken bold/italic markers like "** Home **" -> "**Home**"
   - tighten link syntax like "[ View Home ] ( https ://... )" -> "[View Home](https://...)"
   - normalize multiple spaces and stray zero-width tokens
*/
function repairStreamArtifacts(text = "") {
  let s = String(text);

  // 1) fix spaced protocols
  s = s.replace(/\bhttps?\s*:\s*\/\s*\/\s*/gi, (m) => m.replace(/\s/g, ""));

  // 2) collapse spaces around dots
  s = s.replace(/(\S)\s+\.\s+(\S)/g, "$1.$2");
  s = s.replace(/(\S)\s+\.\s+(\S)/g, "$1.$2");

  // 3) fix spaced slashes
  s = s.replace(/\/\s+/g, "/");

  // 4) collapse excessive spaces
  s = s.replace(/[ \t]{2,}/g, " ");

  // 5) tighten emphasis markers
  s = s.replace(/\*\*\s+([^*]+?)\s+\*\*/g, "**$1**");
  s = s.replace(/\*\s+([^*]+?)\s+\*/g, "*$1*");
  s = s.replace(/_\s+([^_]+?)\s+_/g, "_$1_");
  s = s.replace(/~~\s+([^~]+?)\s+~~/g, "~~$1~~");

  // 6) tighten bracketed links
  s = s.replace(/\[\s*([^\]]+?)\s*\]\s*\(\s*([^)]+?)\s*\)/g, "[$1]($2)");

  // 7) punctuation spacing
  s = s.replace(/\s+([.,!?:;])/g, "$1");

  // 8) collapse multiple newlines
  s = s.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n");

  // 9) remove <think> tags
  s = s.replace(/<\/?think>/g, "");

  // 10) ensure numbered list items are on new lines
s = s.replace(/(\d+)\s*\.\s*/g, "\n$1. ");

  // // 11) ensure dash bullets are on new lines
  // s = s.replace(/\s+-\s+/g, "\n- ");

  return s;
}


/* ------------------ marked renderer (safe code highlighting) ------------------ */
const renderer = new marked.Renderer();

renderer.code = (code, lang) => {
  const raw = typeof code === "string" ? code : code?.text || "";
  const codeText = raw.replace(/\u200b/g, "").trim(); // remove zero-width if any
  let highlighted = "";
  try {
    if (lang && hljs.getLanguage(lang)) {
      highlighted = hljs.highlight(codeText, { language: lang }).value;
    } else {
      highlighted = hljs.highlightAuto(codeText).value;
    }
  } catch (err) {
    // fallback: escape HTML if highlighting fails
    highlighted = escapeHtml(codeText);
  }
  const codeId = `code-${Math.random().toString(36).slice(2)}`;

  return `
    <div class="relative group my-4">
      <button class="copy-code-btn absolute top-2 right-2 text-xs px-2 py-1 bg-gray-700 text-white rounded opacity-0 group-hover:opacity-100 transition" data-target="${codeId}">
        Copy
      </button>
      <pre class="bg-gray-800 text-green-200 p-3 rounded-md overflow-x-auto text-sm">
        <code id="${codeId}" class="hljs language-${escapeHtml(lang || "plaintext")}">${highlighted}</code>
      </pre>
    </div>
  `;
};

renderer.codespan = (code) => {
  const raw = typeof code === "string" ? code : code?.text || "";
  return `<code class="bg-gray-200 text-red-700 px-1 py-0.5 rounded text-xs">${escapeHtml(
    raw
  )}</code>`;
};

/* ------------------ marked options ------------------ */
marked.setOptions({
  gfm: true,
  breaks: false, // keep paragraphs intact; we will preserve real newlines
  renderer,
});

/* ------------------ small nicer pre-format (non-destructive) ------------------ */
const preFormatMarkdown = (text) => {
  if (!text) return "";

  let t = String(text);

  // Trim weird leading/trailing whitespace but keep internal newlines
  t = t.replace(/^\s+|\s+$/g, "");

  // Fix isolated repeated newlines (preserve paragraph separation)
  t = t.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n");

  // Avoid inserting newlines mid-sentence — keep original punctuation spacing
  // (we no longer add newlines after periods)

  return t;
};

/* ------------------ parse -> sanitize ------------------ */
function parseMarkdown(text) {
  const processed = text || "";
  const dirty = marked.parse(processed);
  const safe = DOMPurify.sanitize(dirty);
  return safe;
}

/* ------------------ final React renderer ------------------ */
const renderMarkdown = (rawText, skipFormat = false) => {
  if (!rawText && rawText !== "") return null; // allow empty string
  // 1) repair common streaming/tokenization artifacts
  const repaired = repairStreamArtifacts(rawText || "");
  // 2) gentle formatting pass
  const pre = skipFormat ? repaired : preFormatMarkdown(repaired);
  // 3) parse + sanitize
  const html = parseMarkdown(pre);
  // Return a React element with the html
  return <div className="markdown-body prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />;
};

export default renderMarkdown;
export { preFormatMarkdown, repairStreamArtifacts };
